import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from '../../../common/Select';
import imageCompression from 'browser-image-compression';
import { FileText, ImageOff, Link2, Loader2, Upload, X } from 'lucide-react';
import { uploadToFirebase } from '../../Users/paymentFormUtils';
import { storageEnv } from '../../../../config/firebase';

// Shapes returned by GET /kyc-flow/by-token/{token} (routers/kyc_flow.py).
export type Stage = 'DRAFT' | 'SUBMITTED' | 'ACKNOWLEDGED';
export type SectionKey = 'kyc' | 'bank' | 'nominees' | 'ack';

export interface FieldRow {
    key: string;
    required: boolean;
    kind: string;
    value: any;
    verify_state: 'verified' | 'flagged' | 'pending' | 'n/a';
    remarks?: string | null;
    // Nothing on file yet — stays fillable after submission (utils/kyc_flow.editable_keys).
    empty?: boolean;
}

export interface Allocation {
    nominee: Record<string, any>;
    allocation_percentage: number;
    allocated_amount: number;
}

// What FarmVest sent for the unit (routers/unit_farmvest.py); null = not sent yet.
export interface UnitFarmvest {
    mou_given: boolean | null;
    mou_link: string | null;
    mou_type: string | null;
    // FarmVest set mou_type, so the investor can't change it.
    mou_type_locked?: boolean;
    updated_at: string | null;
}

export interface FlowUnit {
    unit_id: string;
    unit_index: number;
    order_id: string;
    // 'standard' | 'cheque' — the investor's choice, or FarmVest's.
    mou_type: string | null;
    allocations: Allocation[];
    farmvest?: UnitFarmvest;
    status: string;
    // Step 2 before this unit's nominee is submitted: the investor can still change it.
    open?: boolean;
    // FarmVest marked the MOU given: this unit's nominee can't change any more.
    mou_locked?: boolean;
    nominees_submitted_at?: string | null;
    // This unit's own acknowledgment: each unit is confirmed on its own ank_verify link.
    ack?: SectionAckState;
}

export interface Flow {
    investor: { mobile: string; first_name?: string; last_name?: string; submitted_at?: string; acknowledged_at?: string };
    stage: Stage;
    // Onboarding step (utils/kyc_flow.flow_step): kyc → bank_nominees → confirm → complete.
    // Bank and nominees open at bank_nominees, once KYC is verified.
    step: FlowStep;
    step_number: number | null;
    // What a plain submit sends now: KYC (step 1); in step 2 bank, nominees or both (details).
    submit_scope: 'kyc' | 'bank' | 'nominees' | 'details' | null;
    // Step 2 with bank or nominees not submitted yet.
    details_open: boolean;
    // Step 2: each section edits like a draft until the investor submits it on its own.
    bank_open: boolean;
    nominees_open: boolean;
    // Sections filled in and ready to submit; nominees are submitted unit by unit.
    submittable: ('bank' | 'nominees')[];
    submittable_units?: string[];
    version: string;
    sections: Record<SectionKey, string>;
    kyc_fields: FieldRow[];
    bank_fields: FieldRow[];
    kyc_missing: string[];
    bank_missing: string[];
    units: FlowUnit[];
    can_submit: boolean;
    // Any unit with the MOU given: KYC and bank can't change any more.
    mou_locked?: boolean;
    flagged: string[];
    nominee_corrections: { id: string; unit_id: string; unit_index?: number; token: string; remarks?: string; items: { nominee_id: string; fields: string[] }[] }[];
    saved_nominees: Record<string, any>[];
    rules: { max_nominees_per_unit: number; relations: string[]; mou_types: string[] };
    // Per-section acknowledgment: n/a | locked | ready | sent | done (utils/kyc_flow.section_ack).
    acks?: Record<AckSectionKey, SectionAckState>;
    // Set when a staff member is filling this in on the investor's behalf.
    assist?: { by: string; by_name: string } | null;
}

export type FlowStep = 'kyc' | 'bank_nominees' | 'confirm' | 'complete';
export type AckSectionKey = 'kyc' | 'bank' | 'nominees';
export interface SectionAckState { status: string; version: string; acknowledged_at?: string | null; sent_at?: string | null }

export const STANDARD = 'standard';
export const CHEQUE = 'cheque';
export const MOU_TYPES = [CHEQUE, STANDARD];
// MOU type is hidden for now (not asked, shown or verified) — kyc_flow.MOU_TYPE_ENABLED on the server.
export const SHOW_MOU_TYPE = false;
// What people see for each MOU type; the values above are what's stored and sent.
export const MOU_TYPE_LABEL: Record<string, string> = { [CHEQUE]: 'Buy Back 70L', [STANDARD]: 'Standard' };
export const mouTypeLabel = (t?: string | null) => (t ? MOU_TYPE_LABEL[t] || t : '');

// The valuation basis the MOU type replaced, still on nominees saved before it.
export const MARKET = 'Market Value';
export const FIXED = 'Fixed Value';
export const VALUE_TYPE_LABEL: Record<string, string> = { [FIXED]: 'Buy Back', [MARKET]: 'Standard' };
export const valueTypeLabel = (t?: string | null) => (t ? VALUE_TYPE_LABEL[t] || t : '');

export const STATUS: Record<string, { label: string; cls: string }> = {
    complete: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300' },
    draft: { label: 'Draft', cls: 'bg-indigo-50 text-indigo-800' },
    not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-300' },
    no_units: { label: 'No units yet', cls: 'bg-slate-100 text-slate-600' },
    no_nominees: { label: 'Add nominees', cls: 'bg-amber-100 text-amber-900' },
    missing: { label: 'Please fill details', cls: 'bg-amber-100 text-amber-900' },
    in_review: { label: 'In review', cls: 'bg-amber-100 text-amber-900' },
    needs_correction: { label: 'Needs correction', cls: 'bg-red-100 text-red-800' },
    verified: { label: 'Verified', cls: 'bg-emerald-100 text-emerald-900' },
    locked: { label: 'Locked', cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-300' },
    ready: { label: 'Action needed', cls: 'bg-[#1B4332] text-white' },
    sent: { label: 'Action needed', cls: 'bg-[#1B4332] text-white' },
    done: { label: 'Acknowledged', cls: 'bg-[#1B4332] text-white' },
};

// A verified section (or unit) the investor has also acknowledged shows as "Acknowledged".
export const ackedStatus = (status: string, ack?: string | null) => (status === 'verified' && ack === 'done' ? 'done' : status);

export const Chip: React.FC<{ status: string; label?: string }> = ({ status, label }) => {
    const m = STATUS[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
    return <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-bold whitespace-nowrap ${m.cls}`}>{label || m.label}</span>;
};

export const inr = (v: any) => {
    const n = Number(v);
    return v == null || v === '' || isNaN(n) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
};

// Above each form while it can still be submitted: once submitted it can't be edited.
export const SubmitNotice: React.FC<{ className?: string }> = ({ className = '' }) => (
    // The "Important" badge on its own line, the message below it.
    <div role="note" className={`flex flex-col items-start gap-1.5 px-3.5 py-3 rounded-xl bg-red-50 ring-1 ring-red-200 text-red-900 ${className}`}>
        <span className="h-5 px-2 inline-flex items-center rounded-full bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wide">Important</span>
        <p className="text-[12.5px] leading-snug">Once you submit, there is no edit option. Please fill in and check your details properly.</p>
    </div>
);

export const inputCls = (err?: string | boolean, readOnly?: boolean) =>
    // Mobile: taller, light-grey fields with a firmer border (like the CRM's Add Lead form).
    `w-full h-12 max-lg:h-14 px-3.5 max-lg:px-4 rounded-xl border text-[15px] sm:text-sm max-lg:text-base outline-none transition placeholder:text-slate-400 ${readOnly
        ? 'bg-slate-50 border-slate-200 text-slate-600'
        : err
            ? 'bg-red-50/40 border-red-400 focus:ring-4 focus:ring-red-500/10'
            : 'bg-white max-lg:bg-slate-50 border-slate-300 max-lg:border-slate-400 hover:border-slate-400 focus:border-[#1B4332] focus:bg-white focus:ring-4 focus:ring-[#1B4332]/10'}`;

export const Label: React.FC<{ htmlFor?: string; required?: boolean; hideOptional?: boolean; children: React.ReactNode }> = ({ htmlFor, required, hideOptional, children }) => (
    <label htmlFor={htmlFor} className="block text-[13px] max-lg:text-[15px] font-bold text-slate-700 max-lg:text-[#14231B] mb-1.5 max-lg:mb-2">
        {children}{required ? <span className="text-red-600 ml-0.5">*</span> : !hideOptional && <span className="text-slate-400 font-medium ml-1 text-xs">(optional)</span>}
    </label>
);

/**
 * Dropdown for the investor forms — the dashboard's common Select, the same one the
 * Orders page's add-nominee form uses: a panel right below the field, never clipped.
 */
export const SelectField: React.FC<{
    id: string; label: string; value: string; options: string[]; placeholder?: string;
    error?: string | boolean; onChange: (v: string) => void;
}> = ({ id, label, value, options, placeholder, error, onChange }) => (
    <div id={id} aria-label={label}>
        <Select value={value} onChange={onChange}
            options={[{ value: '', label: placeholder || 'Select' }, ...options.map(o => ({ value: o, label: o }))]}
            className={`${inputCls(error)} bg-white`} />
    </div>
);

const isPdfUrl = (url: string) => /\.pdf($|[?#])/i.test(decodeURIComponent(url || ''));

/**
 * A document (photo or PDF) that opens in a viewer over the page instead of a new
 * browser window. Escape, the ✕ or a tap outside closes it.
 */
// A document image that recovers from a failed load. A plain <img> stays broken for
// good when its one request fails (a network blip, or Firebase not serving a file
// uploaded moments ago), while the popup's fresh request then works. This retries
// twice with a cache-busting param, then shows "Tap to view" instead of alt text.
const RETRY_DELAYS_MS = [1000, 3000];
export const DocImg: React.FC<{ src: string; alt: string; className?: string; lazy?: boolean }> = ({ src, alt, className, lazy }) => {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    useEffect(() => { setAttempt(0); setFailed(false); }, [src]);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => () => clearTimeout(timer.current), []);
    if (failed) {
        return <span className="w-full h-full min-h-[64px] flex flex-col items-center justify-center gap-1 text-slate-500" title={alt}>
            <ImageOff size={20} /><span className="text-[11px] font-bold">Tap to view</span></span>;
    }
    const url = attempt ? `${src}${src.includes('?') ? '&' : '?'}_retry=${attempt}` : src;
    return <img src={url} alt={alt} loading={lazy ? 'lazy' : undefined} className={className}
        onError={() => {
            if (attempt >= RETRY_DELAYS_MS.length) { setFailed(true); return; }
            timer.current = setTimeout(() => setAttempt(a => a + 1), RETRY_DELAYS_MS[attempt]);
        }} />;
};

export const DocViewer: React.FC<{
    url: string; label: string; className?: string; children: React.ReactNode; stopPropagation?: boolean;
}> = ({ url, label, className, children, stopPropagation }) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
    }, [open]);
    const pdf = isPdfUrl(url);
    return (
        <>
            <button type="button" aria-label={`View ${label}`} className={className}
                onClick={e => { if (stopPropagation) e.stopPropagation(); setOpen(true); }}>{children}</button>
            {open && createPortal(
                // A centred popup above everything, including the dashboard's top bar and sidebar.
                <div className="fixed inset-0 z-[100000] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={label}
                    onClick={() => setOpen(false)}>
                    <div className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 pl-4 pr-2 py-2 border-b border-slate-100">
                            <b className="flex-1 min-w-0 truncate text-sm text-slate-900">{label}</b>
                            <button type="button" onClick={() => setOpen(false)} aria-label="Close" autoFocus
                                className="w-9 h-9 rounded-full text-slate-600 hover:bg-slate-100 grid place-items-center"><X size={20} /></button>
                        </div>
                        <div className="flex-1 min-h-0 flex items-center justify-center bg-slate-50 p-3">
                            {pdf
                                ? <iframe src={url} title={label} className="w-full h-[65vh] rounded-lg bg-white" />
                                : <DocImg src={url} alt={label} className="max-w-full max-h-[70vh] object-contain rounded-lg" />}
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

/** Upload a file to Firebase storage and hand back its https URL. */
export const DocUpload: React.FC<{
    id: string; label: string; url?: string | null; required?: boolean; hideOptional?: boolean; error?: string;
    mobile: string; folder: string; readOnly?: boolean; onChange: (url: string | null) => void;
    // The picked image, as soon as it's ready to upload (e.g. to read details off it with OCR).
    onFile?: (file: File) => void;
    // Staff filling in for the investor: "Upload" or "Paste URL", as the old staff edit form had.
    // A pasted link is saved as it is; its image is fetched only to read details off it (onFile).
    allowUrl?: boolean;
}> = ({ id, label, url, required, hideOptional, error, mobile, folder, readOnly, onChange, onFile, allowUrl }) => {
    const ref = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [pct, setPct] = useState(0);
    const [upErr, setUpErr] = useState('');
    const [mode, setMode] = useState<'file' | 'url'>('file');
    const [urlInput, setUrlInput] = useState('');
    const loadUrl = async () => {
        const link = urlInput.trim();
        if (!/^https:\/\/\S+$/i.test(link)) { setUpErr('Paste a link starting with https://'); return; }
        setUpErr(''); setUrlInput('');
        onChange(link);
        if (!onFile) return;
        // Best effort: links that don't allow downloading just skip reading the details.
        try {
            const res = await fetch(link);
            if (!res.ok) return;
            const blob = await res.blob();
            if (blob.type.startsWith('image/')) onFile(new File([blob], `pasted_${Date.now()}.${blob.type.includes('png') ? 'png' : 'jpg'}`, { type: blob.type }));
        } catch { /* not readable from here */ }
    };
    const pick = async (file: File) => {
        setBusy(true); setUpErr(''); setPct(0);
        try {
            let f = file;
            if (file.type.startsWith('image/')) {
                try { f = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true }); } catch { /* original */ }
            }
            if (f.size > 5 * 1024 * 1024) throw new Error('File is larger than 5 MB');
            const path = `animalkart/${storageEnv}/${mobile}/${folder}/${Date.now()}_${file.name.replace(/[^A-Za-z0-9._-]/g, '_')}`;
            // OCR (onFile) starts alongside the upload, as on the staff Fill KYC form.
            if (f.type.startsWith('image/')) onFile?.(f);
            onChange(await uploadToFirebase(f, path, setPct));
        } catch (e: any) {
            setUpErr(e?.message || 'Upload failed. Please try again.');
        } finally { setBusy(false); }
    };
    const isPdf = (url || '').toLowerCase().includes('.pdf');
    return (
        <div>
            <Label htmlFor={id} required={required} hideOptional={hideOptional}>{label}</Label>
            {allowUrl && !readOnly && !url && (
                <div className="mb-2 flex p-0.5 rounded-lg bg-slate-100 ring-1 ring-slate-200 text-[12px]" role="tablist" aria-label={`${label}: how to add`}>
                    {([['file', 'Upload'], ['url', 'Paste URL']] as const).map(([m, text]) => (
                        <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setUpErr(''); }}
                            className={`flex-1 h-8 rounded-md font-bold ${mode === m ? 'bg-white text-[#1B4332] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{text}</button>
                    ))}
                </div>
            )}
            {url ? (
                <div className="relative h-44 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                    <DocViewer url={url} label={label} className="block w-full h-full">
                        {isPdf
                            ? <span className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-[#1B4332]"><FileText size={32} /><span className="text-xs font-bold">PDF uploaded · tap to view</span></span>
                            : <DocImg src={url} alt={label} className="w-full h-full object-contain" />}
                    </DocViewer>
                    {!readOnly && (
                        <button type="button" onClick={() => onChange(null)} aria-label={`Remove ${label}`}
                            className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/75">
                            <X size={18} />
                        </button>
                    )}
                </div>
            ) : allowUrl && mode === 'url' && !readOnly ? (
                <div className="flex gap-2">
                    <div className="relative flex-1 min-w-0">
                        <Link2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <input id={id} type="url" inputMode="url" value={urlInput} placeholder="Paste image URL (https://…)"
                            onChange={e => setUrlInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); loadUrl(); } }}
                            className={`w-full h-11 pl-9 pr-3 rounded-xl border text-sm outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 ${error || upErr ? 'border-red-400' : 'border-slate-300'}`} />
                    </div>
                    <button type="button" onClick={loadUrl} disabled={!urlInput.trim()}
                        className="h-11 px-4 rounded-xl bg-[#1B4332] text-white text-sm font-bold disabled:opacity-40">Load</button>
                </div>
            ) : (
                <button id={id} type="button" disabled={readOnly || busy} onClick={() => ref.current?.click()}
                    className={`w-full min-h-[96px] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 px-3 text-center transition ${error || upErr ? 'border-red-400 bg-red-50/40' : 'border-slate-300 bg-white hover:border-[#1B4332] hover:bg-emerald-50/30'} disabled:opacity-60`}>
                    {busy ? <><Loader2 size={20} className="animate-spin text-[#1B4332]" /><span className="text-xs font-bold text-[#1B4332]">Uploading {pct}%</span></>
                        : <><Upload size={20} className="text-[#2D6A4F]" /><span className="text-[13px] font-bold text-slate-800">Take photo or upload</span><span className="text-[11px] text-slate-500">JPG, PNG or PDF · max 5 MB</span></>}
                </button>
            )}
            <input ref={ref} type="file" accept="image/*,application/pdf" capture={undefined} hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }} />
            {(error || upErr) && <p className="mt-1 text-xs font-semibold text-red-700">{error || upErr}</p>}
        </div>
    );
};

export const RemarkBox: React.FC<{ text?: string | null }> = ({ text }) => text ? (
    <div className="mt-2 mb-1 px-3 py-2 rounded-lg bg-red-50 text-red-900 text-[13px]"><b>Our team:</b> {text}</div>
) : null;
